import {
  ChangeDetectionStrategy,
  Component,
  Input,
  computed,
  signal
} from '@angular/core';

import { ProgressBarBlock } from '@core/services/ai/ai-assistant.service';

@Component({
  selector: 'app-ai-progress-bar-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  template: `
    @if (block.title) {
      <div class="pb__title">{{ block.title }}</div>
    }
    <div class="pb" [attr.data-tone]="block.tone || 'neutral'">
      <div class="pb__head">
        <span class="pb__label">{{ block.label }}</span>
        <span class="pb__value">{{
          block.valueLabel ?? clampedPct() + '%'
        }}</span>
      </div>
      <div class="pb__track">
        <div class="pb__fill" [style.width.%]="clampedPct()"></div>
      </div>
      @if (block.hint) {
        <div class="pb__hint">{{ block.hint }}</div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .pb__title {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(15, 23, 42, 0.55);
        margin: 0 0 8px;
      }
      .pb {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 12px 14px;
      }
      .pb__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;
      }
      .pb__label {
        font-weight: 600;
        font-size: 13px;
        color: rgba(15, 23, 42, 0.85);
        overflow-wrap: anywhere;
      }
      .pb__value {
        font-weight: 700;
        font-size: 14px;
        color: rgba(15, 23, 42, 0.95);
        white-space: nowrap;
      }
      .pb__track {
        height: 10px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.06);
        overflow: hidden;
      }
      .pb__fill {
        height: 100%;
        background: linear-gradient(90deg, #6366f1, #8b5cf6);
        border-radius: 999px;
        min-width: 2px;
        transition: width 0.4s ease;
      }
      .pb[data-tone='positive'] .pb__fill {
        background: linear-gradient(90deg, #10b981, #059669);
      }
      .pb[data-tone='warning'] .pb__fill {
        background: linear-gradient(90deg, #f59e0b, #d97706);
      }
      .pb[data-tone='danger'] .pb__fill {
        background: linear-gradient(90deg, #ef4444, #dc2626);
      }
      .pb__hint {
        margin-top: 6px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.55);
      }
    `
  ]
})
export class AiProgressBarBlockComponent {
  private readonly _block = signal<ProgressBarBlock | null>(null);

  @Input({ required: true })
  set block(value: ProgressBarBlock) {
    this._block.set(value);
  }
  get block(): ProgressBarBlock {
    return this._block()!;
  }

  readonly clampedPct = computed(() => {
    const b = this._block();
    if (!b) return 0;
    return Math.max(0, Math.min(100, Math.round(b.percent)));
  });
}
